import moment from '@g4/moment-utc';
import isEmpty from 'lodash/isEmpty';
import tracking from '@g4/g4-analytics';
import { isPartialHotelsStay } from '../hotelsPage/hotelsPageAnalytics';
import { isPartialRentalCar } from '../carsPage/carsPageAnalytics';
import { GA4_MAX_PAYLOAD_LIMIT, ALLEGIANT_FIRST, ALLEGIANT_FIRST_BUNDLE, NEW_FARE_TIER, MAIN, ALLEGIANT_FIRST_NAME} from '../indexPage/constants';
import { ssrCodes } from '../ancillariesPage/constants';
import {
  DEPARTING,
  RETURNING,
  tripTypeValues,
} from '../../utils/constants/trackingConstants';
import { removeBrandFromBundleName } from '../bundlesPage/getBundleRawProducts';

const getDestinationAdvertTrackingdata = (promoData) => {
  const creativeName = promoData.haveImage
    ? promoData.isMobile
      ? promoData.advertImageResponsive
        ? promoData.advertImageResponsive.split('/').slice(-1)[0]
        : 'missing responsive image'
      : promoData.advertImageDesktop
      ? promoData.advertImageDesktop.split('/').slice(-1)[0]
      : 'missing desktop image'
    : 'No image';

  const advertName = promoData.advertName
    ? promoData.advertName + '|' + promoData.cta
    : promoData.alt
    ? promoData.alt
    : '';
  const contentGroup = tracking.getDataLayerVars().content_group;
  const impressionData = {
    creative: creativeName,
    name: advertName,
    position: `${
      promoData.positionGAValue ||
      'Destination Advert - missing positionGAValue attribute'
    }-${contentGroup}`,
    id: '',
    items: [
      {
        item_id: '', // Yet to Decide
        item_name: creativeName,
        coupon: '', // Yet to Decide
        discount: '', // Yet to Decide
        affiliation: 'WWW - Booking',
        index: 1,
        item_brand:
          (promoData.operatedBy && promoData.operatedBy?.carrier) || 'G4',
        item_category: '', // Yet to Decide
        item_category2: '',
        item_category3: '', // Yet to Decide
        item_category4: promoData.route,
        item_category5: promoData.affinityString,
        price: '', // Yet to Decide
        quantity: 1,
        item_promotion_name: advertName,
        item_promotion_id: '', // Yet to Decide
      },
    ],
  };

  return impressionData;
};

//Function to get approx size of individual object in bytes
const getSizeOfObject = (object) => {
  const objectList = [];
  const stack = [object];
  let bytes = 0;

  while (stack.length) {
    const value = stack.pop();

    switch (typeof value) {
      case 'boolean':
        bytes += 4;
        break;
      case 'string':
        bytes += value.length * 2;
        break;
      case 'number':
        bytes += 8;
        break;
      case 'object':
        if (value !== null && !objectList.includes(value)) {
          objectList.push(value);
          for (const prop in value) {
            if (Object.prototype.hasOwnProperty.call(value, prop)) {
              bytes += prop.length * 2;
              stack.push(value[prop]);
            }
          }
        }
        break;
      default:
        break;
    }
  }

  return bytes;
};

//Function to get size of array of objects in bytes
const getSizeOfArrayObjects = (array) => {
  return array.reduce((total, obj) => total + getSizeOfObject(obj), 0);
};
const payLoadLimit = GA4_MAX_PAYLOAD_LIMIT;

const shouldSkipBundledAncillary = (item) => {
    // Only check ancillary items
    if (item.__typename !== 'TravelerAncillaryOrderItem' && 
        item.__typename !== 'ItineraryAncillaryOrderItem') {
      return false;
    }

    // Skip bundled ancillaries completely if they are exactly what's included in the bundle
    // Only track bundled ancillaries if there are extras (quantity > 1)
    return item?.isBundledAncillaryIncluded === true && (item.quantity || 1) <= 1;
  };

export const getCartRawProducts = (
  cart,
  operatedBy,
  flightSearchCriteria,
  seatSizesMap,
  googleAnalyticsData,
  eventName
) => {
  const items = cart?.order?.items;
  if (!items || isEmpty(flightSearchCriteria)) {
    return [];
  }
  const {
    adultsCount,
    childrenCount,
    infantCount,
    lapInfantCount,
    tripType,
    departDate,
    returnDate,
    origin,
    destination,
  } = flightSearchCriteria;

  const flightMarketsGAData = {
    departing: googleAnalyticsData?.flightMarkets?.[0],
    returning: googleAnalyticsData?.flightMarkets?.[1],
  };

  const getSeatName = (seatId, items) => {
    // checks for SeatOrderItem and seatSizeId from the order
    const seat = items.find(
      (item) =>
        item.__typename === 'SeatOrderItem' && item.seatSizeId === seatId
    );
    if (seat) {
      // checks for the FlightOrderItem and flightId from seatSizesMap
      const flightForSeat = seatSizesMap?.order?.items.find(
        (item) =>
          item.__typename === 'FlightOrderItem' &&
          seat.flightId === item.flight.id
      );
      // returns the name of the seat selected
      const name = flightForSeat?.seatmap?.seatSizesMap?.find(
        (seatSize) => seatSize?.id === seatId
      )?.name;
      if (name === ALLEGIANT_FIRST_BUNDLE) return ALLEGIANT_FIRST;
      return name;
    }
  };

  const ancillaryAffinity = {
    PRIORITY_BOARDING: 'Priority Boarding',
    CHECK_IN_BAG: 'Bags/Checked Bags',
    CARRY_ON_BAG: 'Bags/Carry On Bag',
    TRIP_FLEX: 'Trip Flex',
    PETC: 'Pet in Cabin',
  };

  const ancillaryTypesName = {
    PRIORITY_BOARDING: 'Priority Boarding',
    CHECK_IN_BAG: 'Checked Bags',
    CARRY_ON_BAG: 'Carry On Bag',
    TRIP_FLEX: 'Trip Flex',
    PETC: 'Pet in Cabin',
  };

  const ancillaryVariant = {
    PRIORITY_BOARDING: 'PB',
    CHECK_IN_BAG: 'Checked Bags',
    CARRY_ON_BAG: 'Carry On Bag',
    TRIP_FLEX: 'Trip Flex',
    PETC: 'PETC',
  };

  const TRAVEL_INSURANCE = 'Travel Insurance';

  let itemPartialReservation = false;
  let flightOrderItem = [];
  let searchCriteria = {};
  let flightDirection, directionLabel, route, flight, flightNumber, flightDate;
  const totalPaxCount =
    adultsCount + childrenCount + infantCount + lapInfantCount;

  const rawProducts = [];
  let splitRawProducts = [];

  const petInCabinItems = [];

  const travelers = cart?.order?.travelers || [];
  const flightOrderItems = cart?.order?.items.filter(
    (item) => item.__typename === 'FlightOrderItem'
  );
  const petInCabinSsrs = travelers.flatMap((traveler) => {
    return traveler.ssrs.filter((ssr) => ssr.code === 'PETC');
  });

  flightOrderItems.forEach((flightOrderItem) => {
    const filteredPetInCabinSsrs = petInCabinSsrs.filter(
      (ssr) => ssr.flightId === flightOrderItem.flight.id
    );
    const petInCabinSsrQuantity = filteredPetInCabinSsrs.length;

    if (petInCabinSsrQuantity > 0) {
      // Find the price for the current flight's PETC SSR
      const currentFlightPetInCabinSsr = petInCabinSsrs.find(
        (ssr) => ssr.flightId === flightOrderItem.flight.id
      );
      const pricePerSsr = currentFlightPetInCabinSsr?.price?.amount || 0;
      petInCabinItems.push({
        ancillaryType: 'PETC',
        price: pricePerSsr * petInCabinSsrQuantity,
        __typename: 'PETC',
        flightId: flightOrderItem.flight.id,
      });
    }
  });

  // code to merge items and PET_IN_CABIN ssr items into single array
  const combinedItems = [...items, ...petInCabinItems];

  const getItemData = (item, index) => {
    let bundle,
      ancillaryName,
      seatName,
      itemId,
      itemVariant,
      productAffinity,
      bundleName;

    const type = item.__typename;
    const affinityString = getAffinityString(cart?.order);
    let itemData = {};
    let itemSpecificData = {};

    switch (type) {
      case 'FlightOrderItem':
        flightOrderItem.push(item);
        flight = items[index]?.flight;
        flightDirection = origin === flight?.origin?.code ? 'D' : 'R';
        directionLabel = flightDirection === 'D' ? DEPARTING : RETURNING;
        route =
          flightDirection === 'D' ? origin + destination : destination + origin;

        flightNumber =
          flightDirection === 'D'
            ? items[0].flight.number
            : items[1].flight.number;
        flightDate =
          flightDirection === 'D'
            ? items[0].flight.arrivalTime.split('T')[0]
            : items[1].flight.arrivalTime.split('T')[0];

        const bundleTierForFlight = items.find(
          (orderItem) =>
            orderItem.__typename === 'BundleOrderItem' &&
            orderItem.flightId === item?.flight?.id
        )?.bundle?.tier;
        const fareBundleLabel =
          bundleTierForFlight === NEW_FARE_TIER ? ALLEGIANT_FIRST : MAIN;

        itemData = {
          itemId: `${route}-${item.flight.id}-${flightDirection}-${fareBundleLabel}`,
          itemName: `${route}-${fareBundleLabel}`,
          itemPrice: item?.flightPrice?.total,
          itemCategory: 'Flights',
          itemCategory2: `${directionLabel} Flight`,
          itemCategory5: affinityString,
          itemFocusCity:
            flightMarketsGAData[directionLabel.toLowerCase()]?.itemFocusCity,
          itemVariant: `${directionLabel} Flight-${route}-${flightNumber}-${flightDate}-${fareBundleLabel}`,
          itemHaul: flightMarketsGAData[directionLabel.toLowerCase()]?.itemHaul,
          itemMarketType:
            flightMarketsGAData[directionLabel.toLowerCase()]?.itemMarketType,
        };
        break;

      case 'BundleOrderItem':
        bundle = items[index]?.bundle;
        bundleName = removeBrandFromBundleName(bundle?.name);
        
        if (bundleName === ALLEGIANT_FIRST_NAME)
          bundleName = ALLEGIANT_FIRST;

        flightDirection =
          !item?.flightId || item?.flightId === items[0].flight.id ? 'D' : 'R';
        route =
          flightDirection === 'D' ? origin + destination : destination + origin;
        flightNumber =
          flightDirection === 'D'
            ? items[0].flight.number
            : items[1].flight.number;
        flightDate =
          flightDirection === 'D'
            ? items[0].flight.arrivalTime.split('T')[0]
            : items[1].flight.arrivalTime.split('T')[0];
        itemVariant = `${bundleName}-${route}-${flightNumber}-${flightDate}`;

        itemData = {
          itemId: `${route}|${bundleName}`,
          itemName: bundleName,
          itemPrice: item?.price?.amount,
          itemCategory: 'Air Ancillary',
          itemCategory2: bundleName,
          itemCategory5: affinityString,
          itemVariant: itemVariant,
        };
        break;

      case 'TravelerAncillaryOrderItem':
      case 'ItineraryAncillaryOrderItem':
        productAffinity = ancillaryAffinity[item.ancillaryType];
        ancillaryName = ancillaryTypesName[item.ancillaryType];

        //Set flightDirection as D for tripflex
        flightDirection =
          !item?.flightId || item?.flightId === items[0].flight.id ? 'D' : 'R';
        route =
          flightDirection === 'D' ? origin + destination : destination + origin;
        flightNumber =
          flightDirection === 'D'
            ? items[0].flight.number
            : items[1].flight.number;
        flightDate =
          flightDirection === 'D'
            ? items[0].flight.arrivalTime.split('T')[0]
            : items[1].flight.arrivalTime.split('T')[0];

        itemId =
          `${ancillaryName}-${route}-${item.flightId ?? items[0].flight.id}` +
          (ancillaryName !== 'Trip Flex' ? `-${item.travelerId}` : '');

        itemVariant = `${ancillaryVariant[item.ancillaryType]}-${route}-${
          flightNumber ?? items[0].flight.id
        }-${flightDate}`;

        itemData = {
          itemId: itemId,
          itemName: ancillaryName,
          itemPrice: item?.price?.amount,
          itemCategory: 'Air Ancillary',
          itemCategory2: productAffinity,
          itemCategory5: affinityString,
          itemVariant: itemVariant,
        };
        break;

      case 'SeatOrderItem':
        seatName = item.row + item.column;
        flightDirection = item.flightId === items[0].flight.id ? 'D' : 'R';
        route =
          flightDirection === 'D' ? origin + destination : destination + origin;

        flightNumber =
          flightDirection === 'D'
            ? items[0].flight.number
            : items[1].flight.number;
        flightDate =
          flightDirection === 'D'
            ? items[0].flight.arrivalTime.split('T')[0]
            : items[1].flight.arrivalTime.split('T')[0];

        itemData = {
          itemId: `${seatName}-${route}-${item.flightId}-${flightDirection}`,
          itemName: `${getSeatName(item.seatSizeId, items)}-${seatName}`,
          itemPrice: item?.seatPrice?.subtotal,
          itemCategory: 'Seats',
          itemCategory2: getSeatName(item.seatSizeId, items),
          itemCategory5: affinityString,
          itemVariant: `${getSeatName(
            item.seatSizeId,
            items
          )}-${seatName}-${route}-${flightNumber}-${flightDate}`,
        };
        break;

      case 'HotelOrderItem':
        itemPartialReservation = isPartialHotelsStay(
          flightOrderItem,
          item.checkin.time,
          item.checkout.time
        );
        flightDirection = 'D';
        route = origin + destination;
        itemData = {
          itemId: item?.roomId,
          itemName: item.hotel.name,
          itemPrice: item.actualPrice?.total,
          itemCategory: '3rd Party/Hotels',
          itemCategory2: item.hotel.name,
          itemCategory5: affinityString,
          itemVariant: `${item.roomId ?? item.hotelId}-${item.roomType}`,
          itemPromoName: item.hotel.promos[0]?.headlineDescription,
        };

        if (item?.hotelPrice?.totalMandatoryFees >= 0) {
          itemSpecificData.item_mandatory_fees_required = item.hotelPrice
            .totalMandatoryFees
            ? 'Yes'
            : 'No';

          itemSpecificData.item_mandatory_fees_amount =
            item.hotelPrice.totalMandatoryFees;

          // Some queries will have price instead of hotelPrice
        } else if (item?.price?.totalMandatoryFees >= 0) {
          itemSpecificData.item_mandatory_fees_required = item.price
            .totalMandatoryFees
            ? 'Yes'
            : 'No';

          itemSpecificData.item_mandatory_fees_amount =
            item.price.totalMandatoryFees;
        }

        break;

      case 'VehicleOrderItem':
        searchCriteria = {
          startDay: moment(item.pickUpDate).format('YYYY-MM-DD'),
          endDay: moment(item.dropOffDate).format('YYYY-MM-DD'),
        };

        itemPartialReservation = isPartialRentalCar(
          flightOrderItem,
          searchCriteria
        );
        flightDirection = 'D';
        route = origin + destination;
        itemData = {
          itemId: item.vehicle.code,
          itemName: item.vendor.name,
          itemPrice: item.vehiclePrice.total.amount,
          itemCategory: '3rd Party/Cars',
          itemCategory2: `${item.vehicle.category}`,
          itemCategory5: affinityString,
          itemVariant: `${item.vehicle.category}-${item.vehicle.type}-${destination}`,
          itemPromoName: item.promotions[0]?.headlineDescription,
        };
        itemSpecificData = {
          item_mandatory_fees_required:
            item.vehiclePrice.taxesAndFeesDueAtRentalSite?.amount > 0
              ? 'Yes'
              : 'No',
          item_mandatory_fees_amount:
            item.vehiclePrice.taxesAndFeesDueAtRentalSite?.amount || 0,
        };
        break;

      case 'TravelInsuranceOrderItem':
        flightDirection = 'D';
        itemData = {
          itemId: `${TRAVEL_INSURANCE}-${origin}${destination}-${departDate?.date}-D`,
          itemName: `${TRAVEL_INSURANCE}/${origin}${destination}`,
          itemPrice: item?.price?.amount || 0,
          itemCategory: TRAVEL_INSURANCE,
          itemCategory2: TRAVEL_INSURANCE,
          itemCategory5: affinityString,
          itemBrand: 'Allianz',
          itemVariant: '',
        };
        break;

      case 'PETC':
        productAffinity = ancillaryAffinity[item.ancillaryType];
        ancillaryName = ancillaryTypesName[item.ancillaryType];
        flightDirection = item.flightId === items[0].flight.id ? 'D' : 'R';
        route =
          flightDirection === 'D' ? origin + destination : destination + origin;
        flightNumber =
          flightDirection === 'D'
            ? items[0].flight.number
            : items[1].flight.number;
        flightDate =
          flightDirection === 'D'
            ? items[0].flight.arrivalTime.split('T')[0]
            : items[1].flight.arrivalTime.split('T')[0];

        itemData = {
          itemId: `${ancillaryName}-${route}-${flightNumber}-${flightDate}`,
          itemName: ancillaryName,
          itemCategory: 'Air Ancillary',
          itemCategory2: productAffinity,
          itemCategory5: affinityString,
          itemVariant: `${
            ancillaryVariant[item.ancillaryType]
          }-${route}-${flightNumber}-${flightDate}`,
          itemPrice: item.price,
        };
        break;

      default:
        itemData = {
          itemId: '',
          itemName: '',
          itemPrice: '',
          itemCategory: '',
          itemCategory2: '',
          itemCategory5: '',
          itemVariant: '',
        };
        break;
    }

    return { itemData, itemSpecificData };
  };

  let purchaseEventAdditionalData = {};
  let processedItemCount = 0; // Track actually processed items

  combinedItems.forEach((item, index) => {
    // Skip bundled ancillaries completely - don't process them
    if (shouldSkipBundledAncillary(item)) {
      return;
    }

    const { itemData, itemSpecificData } = getItemData(item, index);
    const itemDiscount = item?.flightPrice?.discountValue?.amount ?? 0;
    const itemBrand =
      itemData.itemBrand ?? ((operatedBy && operatedBy?.carrier) || 'G4');
    const bookingDaysOut = moment(departDate.date).diff(moment(), 'days');

    purchaseEventAdditionalData =
      eventName === 'purchase'
        ? {
            item_haul:
              itemData.itemHaul ??
              flightMarketsGAData['departing']?.itemHaul ??
              route,
            item_market_type:
              itemData.itemMarketType ??
              flightMarketsGAData['departing']?.itemMarketType ??
              route,
            item_focus_city:
              itemData.itemFocusCity ??
              flightMarketsGAData['departing']?.itemFocusCity ??
              route,
          }
        : {};

    const rawProduct = {
      item_id: `${itemData.itemId}-${processedItemCount + 1}`,
      item_name: itemData.itemName,
      coupon: '',
      discount: '',
      index: processedItemCount + 1,
      affiliation: 'WWW - Booking',
      item_brand: itemBrand,
      item_category: itemData.itemCategory,
      item_category2: itemData.itemCategory2,
      item_category3: flightDirection,
      item_category4: route,
      item_category5: itemData.itemCategory5,
      price: itemData.itemPrice,
      item_variant: itemData.itemVariant,
      quantity: 1,
      item_partial_reservation: itemPartialReservation,
      item_loyalty_points_discount: '',
      item_flight_booking_type: tripType,
      item_booking_start: departDate?.date,
      item_booking_end:
        tripType === tripTypeValues.ROUNDTRIP
          ? returnDate?.date
          : departDate?.date,
      item_booking_days_out: bookingDaysOut,
      item_promotion_name: itemData.itemPromoName ?? '',
      item_count_child: childrenCount,
      item_count_infant: infantCount,
      item_count_lap_child: lapInfantCount,
      item_count_adult: adultsCount,
      item_count_total: totalPaxCount,
      item_credit_applied: '',
      item_flight_voucher_discount: itemDiscount,
      item_round_trip_discount: itemDiscount,
      ...purchaseEventAdditionalData,
      // include item specific data if any
      ...(!isEmpty(itemSpecificData) ? itemSpecificData : {}),
    };

    splitRawProducts.push(rawProduct);
    processedItemCount++; // Increment count of actually processed items

    //Track view_cart as multiple hits when payload exceeds maximum limit
    if (getSizeOfArrayObjects(splitRawProducts) > payLoadLimit) {
      rawProducts.push(splitRawProducts);
      splitRawProducts = [];
    }
  });

  // Push remaining items if any were processed
  if (splitRawProducts.length > 0) {
    rawProducts.push(splitRawProducts);
  }

  return rawProducts;
};

// Function to get the affinity string
export const getAffinityString = (order) => {
  let affinityString = '';
  if (order?.items) {
    const itemTypes = [
      { typeName: 'FlightOrderItem', label: 'Air' },
      { typeName: 'BundleOrderItem', label: 'Bundle' },
      { typeName: 'HotelOrderItem', label: 'Hotels' },
      { typeName: 'ShowOrderItem', label: 'Game Ticket' },
      { typeName: 'VehicleOrderItem', label: 'Rental Cars' },
      { typeName: 'SeatOrderItem', label: 'Seats' },
      { typeName: 'TravelInsuranceOrderItem', label: 'Travel Insurance' },
    ];
    itemTypes.forEach((itemType) => {
      if (order.items.some((item) => item.__typename === itemType.typeName)) {
        affinityString += `${itemType.label}, `;
      }
    });

    const ancillaryAffinity = {
      CHECK_IN_BAG: 'Checked Bag',
      CARRY_ON_BAG: 'Carry-On Bag',
      PRIORITY_BOARDING: 'PB',
      TRIP_FLEX: 'TP',
    };
    Object.keys(ancillaryAffinity).forEach((ancillaryType) => {
      if (order.items.some((item) => item.ancillaryType === ancillaryType)) {
        affinityString += `${ancillaryAffinity[ancillaryType]}, `;
      }
    });

    const hasPetc = order.travelers.some((traveler) =>
      traveler.ssrs.some((ssr) => ssr.code === ssrCodes.PET_IN_CABIN)
    );
    if (hasPetc) {
      affinityString += 'PETC, ';
    }

    // Remove trailing comma and space
    if (affinityString.length) {
      affinityString = affinityString.slice(0, -2);
    }
  }

  return affinityString;
};

export default getDestinationAdvertTrackingdata;
